import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Star, CheckCircle, ForkKnife } from "@phosphor-icons/react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedbackPage = () => {
  const [orderId, setOrderId] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [overallRating, setOverallRating] = useState(0);
  const [foodRating, setFoodRating] = useState(0);
  const [serviceRating, setServiceRating] = useState(0);
  const [ambianceRating, setAmbianceRating] = useState(0);
  const [comment, setComment] = useState("");
  const [itemRatings, setItemRatings] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oid = params.get("order");
    if (oid) {
      setOrderId(oid);
      loadOrder(oid);
    }
  }, []);

  const loadOrder = async (oid) => {
    setLoading(true);
    try {
      const checkRes = await axios.get(`${API}/feedback/check/${oid}`);
      if (checkRes.data.exists) {
        setAlreadySubmitted(true);
        setLoading(false);
        return;
      }
      // Fetch order details via self-order track (doesn't need auth)
      // We'll use the receipt endpoint if authed, or just show a simple form
      setOrderData({ order_id: oid });
    } catch (e) {
      // Order might not exist
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    if (orderId.length < 6) {
      toast.error("Please enter a valid order ID");
      return;
    }
    await loadOrder(orderId);
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      toast.error("Please provide an overall rating");
      return;
    }
    try {
      const itemRatingsList = Object.entries(itemRatings)
        .filter(([, data]) => data.rating > 0)
        .map(([id, data]) => ({ menu_item_id: id, name: data.name, rating: data.rating }));

      await axios.post(`${API}/feedback`, {
        order_id: orderId,
        overall_rating: overallRating,
        food_rating: foodRating || null,
        service_rating: serviceRating || null,
        ambiance_rating: ambianceRating || null,
        comment: comment || null,
        guest_name: guestName || null,
        item_ratings: itemRatingsList.length > 0 ? itemRatingsList : null
      });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit feedback");
    }
  };

  // Already submitted
  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="py-12">
            <CheckCircle size={48} weight="fill" className="text-green-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl mb-2">Feedback Already Submitted</h2>
            <p className="text-muted-foreground">Thank you! You've already reviewed this order.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success
  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="feedback-success">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="py-12">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }}>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={48} weight="fill" className="text-green-500" />
              </div>
            </motion.div>
            <h2 className="font-heading text-2xl mb-2">Thank You!</h2>
            <p className="text-muted-foreground">Your feedback helps us improve. We appreciate you dining with us!</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No order loaded yet - show lookup
  if (!orderData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="feedback-lookup">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Star size={28} weight="fill" className="text-white" />
            </div>
            <CardTitle className="font-heading text-2xl">Rate Your Experience</CardTitle>
            <p className="text-muted-foreground text-sm">Enter your order ID to leave feedback</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Order ID (last 6 characters)</Label>
              <Input placeholder="abc123" value={orderId} onChange={(e) => setOrderId(e.target.value)} className="font-mono" data-testid="feedback-order-input" />
            </div>
            <Button onClick={handleLookup} className="w-full" disabled={loading} data-testid="feedback-lookup-btn">
              {loading ? "Loading..." : "Continue"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Feedback form
  return (
    <div className="min-h-screen bg-background p-4" data-testid="feedback-form">
      <div className="max-w-lg mx-auto py-6">
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl font-medium">How was your experience?</h1>
          <p className="text-muted-foreground text-sm">Order #{orderId.slice(-6)}</p>
        </div>

        <Card className="mb-4">
          <CardContent className="p-5 space-y-6">
            {/* Guest Name */}
            <div className="space-y-2">
              <Label>Your Name (optional)</Label>
              <Input placeholder="Anonymous" value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="feedback-name" />
            </div>

            {/* Overall Rating */}
            <div>
              <Label className="mb-3 block text-base font-medium">Overall Experience *</Label>
              <StarRating value={overallRating} onChange={setOverallRating} size={36} testId="overall" />
            </div>

            <Separator />

            {/* Category Ratings */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="mb-2 block text-sm">Food Quality</Label>
                <StarRating value={foodRating} onChange={setFoodRating} size={24} testId="food" />
              </div>
              <div>
                <Label className="mb-2 block text-sm">Service</Label>
                <StarRating value={serviceRating} onChange={setServiceRating} size={24} testId="service" />
              </div>
              <div>
                <Label className="mb-2 block text-sm">Ambiance</Label>
                <StarRating value={ambianceRating} onChange={setAmbianceRating} size={24} testId="ambiance" />
              </div>
            </div>

            <Separator />

            {/* Comment */}
            <div className="space-y-2">
              <Label>Comments (optional)</Label>
              <Textarea placeholder="Tell us what you loved or how we can improve..." value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="feedback-comment" />
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSubmit} className="w-full h-12 text-base gap-2" disabled={overallRating === 0} data-testid="submit-feedback-btn">
          <Star size={20} weight="fill" />
          Submit Feedback
        </Button>
      </div>
    </div>
  );
};

// Star Rating Component
const StarRating = ({ value, onChange, size = 24, testId = "" }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-1" data-testid={`stars-${testId}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="transition-transform hover:scale-110 active:scale-95"
          data-testid={`star-${testId}-${star}`}
        >
          <Star
            size={size}
            weight={(hover || value) >= star ? "fill" : "regular"}
            className={(hover || value) >= star ? "text-yellow-500" : "text-border"}
          />
        </button>
      ))}
    </div>
  );
};

export default FeedbackPage;
